/* ──────────────────────────────────────────────────────────────
 *  useAutoClamp — Auto-clamp coreLength & outerSleeveLength
 *
 *  Triggers when dynamic ranges update (bushing type change) or
 *  when SOR string changes (SOR upload / part number selection).
 *  Does NOT trigger on every keystroke — blur handler covers
 *  manual edits.
 * ────────────────────────────────────────────────────────────── */

import { useEffect } from 'react';
import { useInputStore } from '@/stores/useInputStore';
import type { DynamicRanges } from './useParameterRanges';

/**
 * useAutoClamp — Clamps coreLength and outerSleeveLength to valid
 * dynamic ranges whenever ranges or SOR string change.
 */
export function useAutoClamp(dynamicRanges: DynamicRanges) {
  const setPackagingField = useInputStore((s) => s.setPackagingField);
  const sorString = useInputStore((s) => s.sorString);

  useEffect(() => {
    const { coreLength, outerSleeveLength } = useInputStore.getState().packaging;
    const coreLengthRange = dynamicRanges.packaging.coreLength;
    const sleeveLengthRange = dynamicRanges.packaging.outerSleeveLength;

    if (coreLength !== null && coreLengthRange) {
      if (coreLength < coreLengthRange.min) {
        setPackagingField('coreLength', coreLengthRange.min);
      } else if (coreLength > coreLengthRange.max) {
        setPackagingField('coreLength', coreLengthRange.max);
      }
    }

    if (outerSleeveLength !== null && sleeveLengthRange) {
      if (outerSleeveLength < sleeveLengthRange.min) {
        setPackagingField('outerSleeveLength', sleeveLengthRange.min);
      } else if (outerSleeveLength > sleeveLengthRange.max) {
        setPackagingField('outerSleeveLength', sleeveLengthRange.max);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicRanges.packaging, setPackagingField, sorString]);
}
